'use client';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getUserDisplayName, getUserInitials } from '@/lib/formatters';
import type { FilterSelectOption } from './FilterSelect';

/**
 * Minimal workspace-user shape a filter list needs. Deliberately structural so
 * every caller's own user type (gtmSignals' `WorkspaceUser`, workspace members,
 * …) satisfies it without a cast.
 */
export interface FilterUser {
  user_id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  profile_pic_url?: string | null;
}

interface WorkspaceUserOptionConfig {
  /** Marks the matching row "(me)". Compared against `user_id`. */
  currentUserId?: string;
  /** Marks the matching row "(me)". Compared case-insensitively against `email`. */
  currentUserEmail?: string;
  /**
   * Which field becomes the option value. Assignment-style filters key on
   * `user_id`; participant-style filters (meetings) key on `email`.
   * @default 'user_id'
   */
  valueKey?: 'user_id' | 'email';
}

const isCurrentUser = (user: FilterUser, config: WorkspaceUserOptionConfig): boolean => {
  if (config.currentUserId && user.user_id === config.currentUserId) return true;
  if (config.currentUserEmail && user.email) {
    return user.email.toLowerCase() === config.currentUserEmail.toLowerCase();
  }
  return false;
};

/**
 * Build the per-user rows of a workspace-member {@link FilterSelect}: avatar
 * icon, "Name (me)" label, and the email as a search keyword so typing an
 * address finds the person. Callers prepend their own scope rows ("All SDRs",
 * "Unassigned", …) — those differ per surface, the user rows never did.
 */
export const workspaceUserFilterOptions = (
  users: FilterUser[],
  config: WorkspaceUserOptionConfig = {},
): FilterSelectOption[] => users.map((user) => ({
  value: config.valueKey === 'email' ? user.email : user.user_id,
  label: `${getUserDisplayName(user)}${isCurrentUser(user, config) ? ' (me)' : ''}`,
  keywords: user.email ? [user.email] : undefined,
  // The email under the name, so two members whose names collapse to the same
  // label (or a name derived from the email) stay distinguishable.
  description: user.email && getUserDisplayName(user) !== user.email ? user.email : undefined,
  icon: (
    <Avatar className="h-5 w-5">
      <AvatarImage src={user.profile_pic_url || undefined} />
      <AvatarFallback className="bg-slate-200 text-slate-700 text-[10px]">
        {getUserInitials(user)}
      </AvatarFallback>
    </Avatar>
  ),
}));
